import React, { useMemo, useState, useEffect } from 'react';
import { Save, X, Plus } from 'lucide-react';
import { PhotoEvidenceModule } from '../attachments/PhotoEvidenceModule';
import {
  api,
  VEHICLE_DOTACION_CATEGORIES,
  M37_CO_GROUPS,
  isVehicleDotacionMaterial,
  materialRequiresVehicleFlag,
} from '../../services/api';

const isSyntheticSerial = (s) => /^NS-\d+-\d+$/.test(String(s || '').trim());

export const AssignmentForm = ({ materials, people, assignments, brandItems, movements, warehouses = [
  { id: 'popayan', name: 'Popayán' },
  { id: 'santander', name: 'Santander de Quilichao' },
], onSave, onCancel, initialType = 'Asignación' }) => {
  const [formData, setFormData] = useState({
    type: initialType,
    assigneeType: 'person',
    responsibleDoc: '',
    vehicleId: '',
    dotacionCategoryFilter: '',
    materialId: '',
    quantity: 1,
    observations: '',
    serial: '',
    brandName: '',
    evidencePhotos: [],
    warehouseId: warehouses[0]?.id || 'popayan',
    coGroup: 'proyectos',
  });
  const [items, setItems] = useState([]);
  const [statusMsg, setStatusMsg] = useState(null);
  const [serialRowsFromApi, setSerialRowsFromApi] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('Todos');
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadVehicles = async () => {
      setVehiclesLoading(true);
      try {
        const res = await api.vehicles.get({ limit: 500 });
        const rows = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) {
          setVehicles(rows.filter((v) => String(v?.status || 'Activo').toLowerCase() !== 'inactivo'));
        }
      } catch (_) {
        if (!cancelled) setVehicles([]);
      } finally {
        if (!cancelled) setVehiclesLoading(false);
      }
    };
    loadVehicles();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (items.length > 0) {
      onSave({
        items,
        warehouseId: formData.warehouseId,
        type: formData.type,
        assigneeType: formData.assigneeType,
        vehicleId: formData.vehicleId ? Number(formData.vehicleId) : null,
        coGroup: formData.coGroup || '',
      });
      setItems([]);
    } else {
      onSave(formData);
    }
    setFormData({
      type: formData.type,
      assigneeType: formData.assigneeType,
      responsibleDoc: '',
      vehicleId: formData.assigneeType === 'vehicle' ? formData.vehicleId : '',
      dotacionCategoryFilter: formData.dotacionCategoryFilter,
      materialId: '',
      quantity: 1,
      observations: '',
      serial: '',
      brandName: '',
      evidencePhotos: [],
      warehouseId: warehouses[0]?.id || 'popayan',
    });
  };

  const addItem = () => {
    const matIdRaw = formData.materialId;
    const matId = matIdRaw ? parseInt(matIdRaw) : null;
    const assigneeType = formData.assigneeType === 'vehicle' ? 'vehicle' : 'person';
    const vehicleId = assigneeType === 'vehicle' ? Number(formData.vehicleId || 0) : 0;
    const personIdRaw = String(formData.responsibleDoc || '').trim();
    const personId = personIdRaw || null;
    const material = matId ? materials.find(m => m.id === matId) : null;
    if (!matId || !material) {
      setStatusMsg({ type: 'error', text: 'Seleccione un material.' });
      return;
    }
    const qtyNum = parseInt(formData.quantity);
    if (!Number.isFinite(qtyNum) || qtyNum < 1) {
      setStatusMsg({ type: 'error', text: 'Ingrese una cantidad válida (mínimo 1).' });
      return;
    }
    if (assigneeType === 'vehicle' && !vehicleId) {
      setStatusMsg({ type: 'error', text: 'Seleccione un vehículo (catálogo Transportes).' });
      return;
    }
    if (assigneeType === 'person' && !personId) {
      setStatusMsg({ type: 'error', text: 'Seleccione la persona responsable.' });
      return;
    }
    if (assigneeType === 'person' && material && (materialRequiresVehicleFlag(material) || isVehicleDotacionMaterial(material))) {
      setStatusMsg({ type: 'error', text: 'Este material es de dotación vehículo; use destinatario Vehículo.' });
      return;
    }
    if (assigneeType === 'vehicle' && material && !isVehicleDotacionMaterial(material) && !materialRequiresVehicleFlag(material)) {
      setStatusMsg({ type: 'error', text: 'El material no está marcado como dotación de vehículo.' });
      return;
    }
    const materialCode = String(material?.code || '').trim();
    const hasSerialsInBrandItems = materialCode
      ? (brandItems || []).some((b) => String(b?.status || 'Activo') === 'Activo' && String(b?.materialCode || b?.material_code || '').trim() === materialCode && String(b?.serial || '').trim() !== '' && !isSyntheticSerial(b?.serial))
      : false;
    const requiresSerial = Boolean(material?.requiresSerial)
      || (material?.requires_serial === 1)
      || (String(material?.requires_serial || '').toLowerCase() === 'true')
      || hasSerialsInBrandItems
      || (serialRowsFromApi || []).length > 0;
    if (requiresSerial && !(formData.serial || '').trim()) {
      setStatusMsg({ type: 'error', text: 'Este material requiere serial. Seleccione uno antes de agregar.' });
      return;
    }
    const person = personId ? people.find(p => String(p.dni || p.id) === String(personId)) : null;
    const vehicle = vehicleId ? vehicles.find((v) => Number(v.id) === vehicleId) : null;
    const newItem = {
      assigneeType,
      vehicleId: vehicleId || null,
      vehiclePlate: vehicle?.plate || '',
      materialId: matId || null,
      materialName: (material?.name) || (matId ? String(matId) : 'Sin material'),
      responsibleDoc: assigneeType === 'person' ? (person?.dni || personId || null) : null,
      responsibleName: assigneeType === 'vehicle'
        ? (vehicle?.plate ? `Vehículo ${vehicle.plate}` : `Vehículo #${vehicleId}`)
        : (person?.name || (personId ? String(personId) : 'Sin responsable')),
      quantity: parseInt(formData.quantity),
      observations: formData.observations || '',
      serial: (formData.serial || '').trim(),
      brandName: (formData.brandName || '').trim(),
      evidencePhotos: Array.isArray(formData.evidencePhotos) ? formData.evidencePhotos : [],
      warehouseId: formData.warehouseId || null
    };
    setItems(prev => [...prev, newItem]);
    setStatusMsg({ type: 'ok', text: 'Ítem agregado.' });
    // Preserva el tipo seleccionado, la persona responsable y las observaciones para facilitar la carga múltiple
    setFormData(prev => ({ 
      ...prev, 
      materialId: '', 
      quantity: 1, 
      serial: '', 
      brandName: '', 
      evidencePhotos: [] 
    }));
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const selectedMaterial = useMemo(() => {
    const id = parseInt(formData.materialId);
    return materials.find((m) => m.id === id) || null;
  }, [formData.materialId, materials]);

  const selectedHasSerialsInBrandItems = useMemo(() => {
    const code = String(selectedMaterial?.code || '').trim();
    if (!code) return false;
    return (brandItems || []).some((b) => String(b?.status || 'Activo') === 'Activo' && String(b?.materialCode || b?.material_code || '').trim() === code && String(b?.serial || '').trim() !== '' && !isSyntheticSerial(b?.serial));
  }, [brandItems, selectedMaterial]);

  const requiresSerialSelected = useMemo(() => {
    if (!selectedMaterial) return false;
    const reqDb = (selectedMaterial?.requires_serial === 1) || (String(selectedMaterial?.requires_serial || '').toLowerCase() === 'true');
    const hasApi = (serialRowsFromApi || []).length > 0;
    return reqDb || Boolean(selectedMaterial?.requiresSerial) || selectedHasSerialsInBrandItems || hasApi;
  }, [selectedMaterial, selectedHasSerialsInBrandItems, serialRowsFromApi]);

  const materialsForWarehouse = useMemo(() => {
    const wh = String(formData.warehouseId || '').trim();
    if (!wh) return [];
    const dotFilter = String(formData.dotacionCategoryFilter || '').trim();
    const assignee = formData.assigneeType === 'vehicle' ? 'vehicle' : 'person';
    return (materials || []).filter((m) => {
      const stock = ((m.stockByWarehouse || {})[wh] || 0);
      const catOk = categoryFilter === 'Todos' || String(m.category || '') === String(categoryFilter);
      const isVehMat = isVehicleDotacionMaterial(m);
      const assigneeOk = assignee === 'vehicle' ? isVehMat : !isVehMat;
      const dotOk = !dotFilter || String(m.dotacion_category || '') === dotFilter;
      return m.status === 'Activo' && stock > 0 && catOk && assigneeOk && dotOk;
    });
  }, [materials, formData.warehouseId, formData.assigneeType, formData.dotacionCategoryFilter, categoryFilter]);

  const assignablePeople = useMemo(() => (
    // A pedido del usuario: se listan TODOS los colaboradores activos de la
    // base de datos, sin restringir por rol operativo (coordinador) ni por
    // línea de negocio/proyecto — ambos cruces dejaban el selector vacío o
    // con muy pocas opciones y bloqueaban la asignación en la práctica.
    (people || []).filter((p) => (
      String(p?.status || '').trim().toLowerCase() === 'activo'
    ))
  ), [people]);

  const categoriesForWarehouse = useMemo(() => {
    const wh = String(formData.warehouseId || '').trim();
    if (!wh) return [];
    const set = new Set();
    (materials || []).forEach((m) => {
      const stock = ((m.stockByWarehouse || {})[wh] || 0);
      if (m.status === 'Activo' && stock > 0) {
        const cat = String(m.category || '').trim();
        if (cat) set.add(cat);
      }
    });
    return Array.from(set).sort((a,b)=>a.localeCompare(b));
  }, [materials, formData.warehouseId]);

  const materialType = selectedMaterial?.type || null;
  const brandsForMaterial = useMemo(() => {
    const type = materialType;
    const materialId = selectedMaterial?.id || null;
    const materialCode = String(selectedMaterial?.code || '').trim();
    const wh = String(formData.warehouseId || '').trim();
    if (!type || !materialId) return [];

    // 1) Preferir marcas derivadas de seriales API disponibles para ESTE material/bodega
    if (serialRowsFromApi.length > 0) {
      const seen = new Set();
      const unique = [];
      for (const r of serialRowsFromApi) {
        const nm = String(r.display_brand || r.brand || '').trim();
        if (!nm) continue;
        const key = nm.toLowerCase();
        if (!seen.has(key)) { seen.add(key); unique.push({ id: `brand-${key}`, name: nm, status: 'Activo' }); }
      }
      return unique;
    }

    // 2) Marcas configuradas para ESTE material (por código y/o id)
    const filtered = (brandItems || []).filter((b) => {
      if (String(b.status || 'Activo') !== 'Activo') return false;
      const bCode = String(b.materialCode || b.material_code || '').trim();
      const bMatId = Number(b.materialId || b.material_id || 0);
      if (materialCode && bCode) return bCode === materialCode;
      if (bMatId > 0) return bMatId === Number(materialId);
      return false;
    });
    const seen = new Set();
    const unique = [];
    for (const b of filtered) {
      const nm = String(b.name || '').trim();
      if (!nm) continue;
      const key = nm.toLowerCase();
      if (!seen.has(key)) { seen.add(key); unique.push(b); }
    }
    if (unique.length > 0) return unique;

    // 3) Fallback final: marcas de entradas para ESTE material en ESTA bodega
    const fromEntries = [];
    const seen2 = new Set();
    (movements || []).forEach((mv) => {
      if (mv.type !== 'Entrada') return;
      if (Number(mv.materialId) !== Number(materialId)) return;
      if (String(mv.warehouseId || '').trim() !== wh) return;
      const nm = String(mv.brandName || '').trim();
      if (!nm) return;
      const k = nm.toLowerCase();
      if (seen2.has(k)) return;
      seen2.add(k);
      fromEntries.push({ id: `ent-${k}`, name: nm, status: 'Activo' });
    });
    return fromEntries;
  }, [brandItems, materialType, selectedMaterial, serialRowsFromApi, movements, formData.warehouseId]);

  // Seriales filtrados por marca seleccionada (cuando vienen de la API)
  const filteredApiSerials = useMemo(() => {
    if (serialRowsFromApi.length === 0) return [];
    const selected = String(formData.brandName || '').trim().toLowerCase();
    const rows = selected
      ? serialRowsFromApi.filter(r => {
          const nm = String(r.display_brand || r.brand || '').trim().toLowerCase();
          return nm === selected;
        })
      : serialRowsFromApi;
    return rows
      .map(r => String(r.serial || '').trim())
      .filter(s => !!s);
  }, [serialRowsFromApi, formData.brandName]);

  // Carga seriales desde la API en modo lectura y filtra los disponibles (no asignados)
  useEffect(() => {
    const loadSerials = async () => {
      const matId = selectedMaterial?.id;
      const wh = formData.warehouseId || '';
      if (!matId) { setSerialRowsFromApi([]); return; }
      try {
        const res = await api.serials.list({ material_id: matId, warehouse_id: wh });
        const data = Array.isArray(res?.data) ? res.data : [];
        // Excluir seriales ya asignados en esta bodega para este material
        const assigned = new Set(
          (assignments || [])
            .filter(a => a.materialId === matId && a.status === 'Asignado' && (a.serial || '').trim() && a.warehouseId === wh)
            .map(a => String(a.serial).trim())
        );
        const availableRows = data.filter(r => {
          const s = String(r.serial || '').trim();
          if (!s) return false;
          if (isSyntheticSerial(s)) return false;
          return !assigned.has(s);
        });
        setSerialRowsFromApi(availableRows);
      } catch (e) {
        // Fallback: usar brandItems si existen seriales allí
        try {
          const code = String(selectedMaterial?.code || '').trim();
          const assigned = new Set(
            (assignments || [])
              .filter(a => a.materialId === matId && a.status === 'Asignado' && (a.serial || '').trim() && a.warehouseId === wh)
              .map(a => String(a.serial).trim())
          );
          const rows = (brandItems || [])
            .filter((b) => String(b?.status || 'Activo') === 'Activo' && String(b?.materialCode || b?.material_code || '').trim() === code)
            .map((b) => ({
              serial: String(b?.serial || '').trim(),
              brand: String(b?.name || b?.brand || '').trim(),
              display_brand: String(b?.name || b?.brand || '').trim(),
            }))
            .filter((r) => r.serial && !isSyntheticSerial(r.serial) && !assigned.has(r.serial));
          setSerialRowsFromApi(rows);
        } catch (_) {
          setSerialRowsFromApi([]);
        }
      }
    };
    loadSerials();
  }, [selectedMaterial, formData.warehouseId, assignments, brandItems]);

  const availableSerials = useMemo(() => {
    // Preferir los seriales provenientes de la API (filtrados por marca si aplica).
    // Si hay datos desde la API para el material/bodega, respetar el filtro
    // aunque el resultado sea vacío. Solo usar fallback cuando NO hay API.
    if (serialRowsFromApi.length > 0) return filteredApiSerials;
    const matId = selectedMaterial?.id;
    if (!matId) return [];
    const entrySerials = (movements || [])
      .filter(mv => mv.type === 'Entrada' && mv.materialId === matId && (mv.serial || '').trim() && mv.warehouseId === formData.warehouseId)
      .map(mv => (mv.serial || '').trim());
    const assignedSerials = (assignments || [])
      .filter(a => a.materialId === matId && a.status === 'Asignado' && (a.serial || '').trim() && a.warehouseId === formData.warehouseId)
      .map(a => (a.serial || '').trim());
    const set = new Set(entrySerials);
    assignedSerials.forEach(s => set.delete(s));
    return Array.from(set);
  }, [filteredApiSerials, serialRowsFromApi, movements, assignments, selectedMaterial, formData.warehouseId]);

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Destinatario</label>
          <select
            value={formData.assigneeType}
            onChange={(e) => setFormData({
              ...formData,
              assigneeType: e.target.value,
              responsibleDoc: '',
              vehicleId: '',
              materialId: '',
              serial: '',
              brandName: '',
            })}
            className="w-full border rounded px-3 py-2"
          >
            <option value="person">Persona</option>
            <option value="vehicle">Vehículo (dotación carro)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Proyecto (grupo CO)</label>
          <select
            value={formData.coGroup}
            onChange={(e) => setFormData({ ...formData, coGroup: e.target.value })}
            className="w-full border rounded px-3 py-2"
          >
            {M37_CO_GROUPS.map((g) => (
              <option key={g.key} value={g.key}>{g.label}</option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">Reglas M37 de entrega según línea de negocio en Control Operativo.</p>
        </div>
        {formData.assigneeType === 'vehicle' ? (
          <div>
            <label className="block text-sm font-medium mb-1">Vehículo</label>
            <select
              value={formData.vehicleId}
              onChange={(e) => setFormData({ ...formData, vehicleId: e.target.value })}
              className="w-full border rounded px-3 py-2"
              disabled={vehiclesLoading}
            >
              <option value="">{vehiclesLoading ? 'Cargando...' : 'Seleccione placa...'}</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plate}{v.brand ? ` — ${v.brand}` : ''}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium mb-1">Persona responsable</label>
            <select
              value={formData.responsibleDoc}
              onChange={(e) => setFormData({ ...formData, responsibleDoc: e.target.value })}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Seleccione...</option>
              {assignablePeople.map(p => (
                <option key={p.id} value={p.dni || p.id}>{p.name} - {p.dni || p.id}</option>
              ))}
            </select>
            {assignablePeople.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">No hay colaboradores activos registrados.</p>
            )}
          </div>
        )}
        {formData.assigneeType === 'vehicle' && (
          <div>
            <label className="block text-sm font-medium mb-1">Categoría dotación</label>
            <select
              value={formData.dotacionCategoryFilter}
              onChange={(e) => setFormData({ ...formData, dotacionCategoryFilter: e.target.value, materialId: '', serial: '', brandName: '' })}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Todas</option>
              {VEHICLE_DOTACION_CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium mb-1">Bodega</label>
          <select
            value={formData.warehouseId}
            onChange={(e) => setFormData({ ...formData, warehouseId: e.target.value })}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">Seleccione...</option>
            {warehouses.map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Categoría</label>
          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setFormData({ ...formData, materialId: '', serial: '', brandName: '' }); }}
            className="w-full border rounded px-3 py-2"
          >
            <option value="Todos">Todos</option>
            {categoriesForWarehouse.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Material</label>
          <select
            value={formData.materialId}
            onChange={(e) => {
              const selectedId = e.target.value;
              setFormData({
                ...formData,
                materialId: selectedId,
                serial: '',
                brandName: ''
              });
            }}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">Seleccione...</option>
            {materialsForWarehouse.map(m => {
              const wh = String(formData.warehouseId || '').trim();
              const stock = ((m.stockByWarehouse || {})[wh] || 0);
              return (
                <option key={m.id} value={m.id}>
                  {m.code ? `${m.code} - ${m.name}` : `${m.name}`} (Stock: {stock})
                </option>
              );
            })}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Marca (según material)</label>
          {brandsForMaterial.length > 0 ? (
            <select
              value={formData.brandName}
              onChange={(e) => setFormData({ ...formData, brandName: e.target.value, serial: '' })}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Seleccione...</option>
              {brandsForMaterial.map((b) => (
                <option key={b.id} value={b.name}>{b.name}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={formData.brandName || ''}
              onChange={(e) => setFormData({ ...formData, brandName: e.target.value })}
              className="w-full border rounded px-3 py-2"
              placeholder="Sin marcas configuradas para este material"
            />
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Serial del equipo</label>
          {!selectedMaterial ? (
            <input
              type="text"
              value=""
              className="w-full border rounded px-3 py-2 bg-gray-100"
              placeholder="Seleccione un material"
              readOnly
            />
          ) : !requiresSerialSelected ? (
            <input
              type="text"
              value=""
              className="w-full border rounded px-3 py-2 bg-gray-100"
              placeholder="No aplica (material no seriado)"
              readOnly
            />
          ) : availableSerials.length > 0 ? (
            <select
              value={formData.serial}
              onChange={(e) => setFormData({ ...formData, serial: e.target.value })}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Seleccione...</option>
              {availableSerials.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={formData.serial || ''}
              className="w-full border rounded px-3 py-2 bg-gray-100"
              placeholder="Sin seriales disponibles para este material"
              readOnly
            />
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Cantidad</label>
          <input
            type="number"
            value={formData.quantity}
            onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
            className="w-full border rounded px-3 py-2"
            min="1"
          />
        </div>
        <div className="col-span-2 md:col-span-3">
          <div className="mt-4 border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <label className="font-semibold">Observaciones</label>
            </div>
            <textarea
              value={formData.observations}
              onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
              className="w-full border rounded px-3 py-2"
              rows="6"
              placeholder="Motivo de la asignación, condiciones especiales..."
            ></textarea>
          </div>
        </div>
      </div>
      
      <div className="col-span-2">
        <PhotoEvidenceModule
          value={formData.evidencePhotos}
          onChange={(arr) => setFormData({ ...formData, evidencePhotos: arr })}
        />
      </div>
      <div className="mt-6 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-200">
            <tr>
              <th className="p-2 text-left">Material</th>
              <th className="p-2 text-center">Cantidad</th>
              <th className="p-2 text-left">Responsable</th>
              <th className="p-2 text-left">Cédula</th>
              <th className="p-2 text-left">Serie</th>
              <th className="p-2 text-left">Marca</th>
              
              <th className="p-2 text-center">Evidencias</th>
              <th className="p-2 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-4 text-center text-sm text-gray-600">
                  No hay artículos agregados. Utilice "Agregar ítem".
                </td>
              </tr>
            ) : (
              items.map((it, idx) => (
                <tr key={idx} className="border-b hover:bg-gray-50">
                  <td className="p-2">{it.materialName}</td>
                  <td className="p-2 text-center">{it.quantity}</td>
                  <td className="p-2">{it.responsibleName}</td>
                  <td className="p-2">{it.responsibleDoc || '-'}</td>
                  <td className="p-2">{it.serial || '-'}</td>
                  <td className="p-2">{it.brandName || '-'}</td>
                  
                  <td className="p-2 text-center">{Array.isArray(it.evidencePhotos) ? it.evidencePhotos.length : 0}</td>
                  <td className="p-2 text-center">
                    <button type="button" onClick={() => removeItem(idx)} className="text-red-600 hover:underline text-sm">Eliminar</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={addItem}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
        >
          <Plus size={18} /> Agregar artículo
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-2 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg"
        >
          <X size={18} /> Cancelar
        </button>
        <button
          type="submit"
          className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
        >
          <Save size={18} /> Guardar
        </button>
      </div>
      <div className="pt-2 text-center">
        {items.length > 0 && (
          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">{items.length} ítems</span>
        )}
        {statusMsg && (
          <p className={`text-sm mt-1 ${statusMsg.type==='error' ? 'text-red-600' : 'text-green-600'}`}>{statusMsg.text}</p>
        )}
      </div>
    </form>
  );
};
